import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createEditor, type Descendant, Element as SlateElement, Transforms } from 'slate';
import { withHistory } from 'slate-history';
import { withReact } from 'slate-react';
import type { TextProps } from '../components/Text';
import type { FontVariant } from '../types/text';
import { useEditor, useNode } from '@craftjs/core';

export const useSlateTextStyles = (textStyles: Descendant[]) => {
  const { actions } = useEditor();
  const {
    id,
    actions: { setProp },
  } = useNode();
  const [value, setValue] = useState<Descendant[]>(textStyles);
  const [fontVariants, setFontVariants] = useState(collectFontVariants(value));
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);
  const originalTextRef = useRef<string>(getPlainText(textStyles));
  const isUserEditRef = useRef(false);

  useEffect(() => {
    //in case of text styles change we should update fonts used in text list
    const textStyleProp = textStyles ?? [];
    if (compareTextStyles(textStyleProp, value)) {
      setValue(textStyleProp);
      setFontVariants(collectFontVariants(textStyleProp));

      // Update the Slate editor's children to reflect the new textStyles
      // This is necessary because Slate uses initialValue and doesn't automatically sync with prop changes
      editor.children = textStyleProp;
      // Clear selection so it never points at a path that no longer exists (avoids "Cannot find a descendant at path" errors)
      Transforms.deselect(editor);
      editor.onChange();

      // Update original text reference when props change from external source
      if (!isUserEditRef.current) {
        originalTextRef.current = getPlainText(textStyleProp);
      }
      isUserEditRef.current = false;
    }
  }, [textStyles, value, editor]);

  //maybe we can use lodash to compare text styles instead of JSON.stringify
  const compareTextStyles = (newTextStyles: Descendant[], textStyles: Descendant[]) => {
    return JSON.stringify(newTextStyles) !== JSON.stringify(textStyles);
  };

  const handleChange = useCallback(
    (newTextStyles: Descendant[]) => {
      const isAstChange = editor.operations.some((op) => 'set_selection' !== op.type);
      if (!isAstChange) return;

      isUserEditRef.current = true;
      setValue(newTextStyles);

      if (editor.mergeCraftHistoryOnce) {
        editor.mergeCraftHistoryOnce = false;
        actions.history.merge().setProp(id, (prop: TextProps) => {
          if (compareTextStyles(newTextStyles, textStyles)) {
            setFontVariants(collectFontVariants(newTextStyles));
          }
          prop.textStyles = newTextStyles;
        });
      } else {
        setProp((prop: TextProps) => {
          if (compareTextStyles(newTextStyles, textStyles)) {
            setFontVariants(collectFontVariants(newTextStyles));
          }
          prop.textStyles = newTextStyles;
        });
      }
    },
    [setProp, textStyles],
  );

  return {
    value,
    fontVariants,
    handleChange,
    editor,
    resetOriginalText: () => {
      originalTextRef.current = getPlainText(value);
    },
  };
};

//helper function to extract font variants
const collectFontVariants = (value: Descendant[]): FontVariant[] => {
  const variants = new Map<string, FontVariant>();

  value.forEach((node) => {
    if (SlateElement.isElement(node)) {
      node.children.forEach((leaf) => {
        if (leaf.fontFamily) {
          const fontStyle = (leaf.fontStyle as string) || 'normal';
          const fontWeight = (leaf.fontWeight as string) || '400';
          const key = `${leaf.fontFamily}-${fontStyle}-${fontWeight}`;
          if (!variants.has(key)) {
            variants.set(key, {
              fontFamily: leaf.fontFamily as string,
              fontStyle,
              fontWeight,
            });
          }
        }
      });
    }
  });

  return Array.from(variants.values());
};

// Helper function to extract plain text from Slate Descendant[]
const getPlainText = (descendants: Descendant[]): string => {
  return descendants
    .map((node) => {
      if (SlateElement.isElement(node)) {
        return node.children.map((child) => child.text || '').join('');
      }
      return '';
    })
    .join('\n');
};
